<template>
  <div
    class="kk-editcell"
    :class="themeClass"
    :style="{
      ...editCellPosition,
      gridArea: `${refinedActiveCellIndexes[1] + 2} / ${refinedActiveCellIndexes[0] + 2} / ${
        refinedActiveCellIndexes[3] + 3
      } / ${refinedActiveCellIndexes[2] + 3}`,
      ...style,
      ...customStyles,
      ...expandibleElementStyle,
      ...formulaBoxMode,
      zIndex,
    }"
    :data-id="activeCellId"
  >
    <textarea
      id="extendableArea"
      ref="textarea"
      :data-id="activeCellId"
      :value="text"
      :class="{
        'kk-editcell-pointer-disable': editMode === EditModes.Disable,
        'kk-editcell-inward-cursor': editMode === EditModes.InnerEdit,
      }"
      :style="{
        'text-align': textAlign,
        ...textareaStyle,
      }"
      :rows="textAreaRows"
      @input="editCellText"
      @keydown="handleKeyDown"
      @keyup="setRange"
      @mouseenter="onShowComment"
      @blur="blur"
    ></textarea>
    <formula-tooltip
      v-if="editMode !== EditModes.Disable"
      :formula-text="formulaText"
      :cursor-position="cursorPosition"
      :cell-id="id"
    />
    <formula-search
      v-if="showSearchBox"
      :collection="collection"
      :input-text="inputText"
      :styles="position"
      :searched-items="dropDownFilteredList"
      @select-formula="selectFormula"
    />
    <div v-if="isActiveCellLabelVisible" class="kk-editcell__activeCellLabel">{{ getActiveCellLabel }}</div>
  </div>
</template>

<script>
import hotkeys from "hotkeys-js";
import { HotkeyScopes, EditModes, RowNumberWidth, ColumnLetterHeight, keycodeToDirection } from "../../utils/constants";
import DesignerMixin from "./DesignerMixin";
import EditCellMixin from "./EditCellMixin";
import FormulaTooltip from "./formula/FormulaTooltip.vue";
import FormulaSearch from "./formula/FormulaSearch.vue";
import { getDefaultCellType } from "./cell/Utils";
import { defaultThemes } from "./cell/CellConstants";
import { isRangeSelectionAllowed, cellIndexToLabel } from "../../utils/util";
import { ProcessTypes } from "../../utils/icons";
import { hfInstance } from "../../store/Cells";
import { getEditModeValue } from "../../utils/DataTypeUtils";

export default {
  components: { FormulaTooltip, FormulaSearch },
  mixins: [DesignerMixin, EditCellMixin],
  props: ["onHover", "options", "getPxFromRowIndex", "getPxFromColumnIndex"],
  emits: ["select-formula"],
  data() {
    return {
      textAreaRows: 1,
    };
  },
  computed: {
    formulaBoxMode() {
      return this.editCell.formulaBox ? { paddingRight: "10px" } : {};
    },
    isInEditMode() {
      return this.editMode !== EditModes.Disable;
    },
    editMode() {
      return this.$store.state.pdesign.editMode.value;
    },
    editModeObject() {
      return this.$store.state.pdesign.editMode;
    },
    style() {
      return {
        margin: `${this.options.margin ?? 0}px`,
        borderRadius: `${this.options.borderRadius ?? 0}px`,
        ...(this.current.type === ProcessTypes.Grid ? { fontSize: "14px" } : {}),
      };
    },
    content() {
      if (this.cell.isMerged) return this.cellsMetaData[this.id.replace("_merged", "")]?.content;
      return this.cell?.content;
    },
    position() {
      const tooltipHeight = document.querySelector(".formula_tooltip-container")?.getBoundingClientRect().height ?? 0;
      return {
        top: 100 + tooltipHeight + "%",
      };
    },
    getActiveCellLabel() {
      let activeCellLabel = null;
      if (this.mergedCellIds[this.activeCellId]) {
        let mergedCellsId = this.mergedCellIds[this.activeCellId].replace("_merged", "");
        let [columnIndex, rowIndex] = this.getCellIndexById(mergedCellsId);
        activeCellLabel = cellIndexToLabel(columnIndex, rowIndex);
      } else activeCellLabel = cellIndexToLabel(this.columnIndex, this.rowIndex);
      return activeCellLabel;
    },
    editCellPosition() {
      let columnLeft = null;
      let rowTop = null;
      const container = document.querySelector(".table-content");
      const containerBound = container.getBoundingClientRect();

      if (this.mergedCellIds[this.activeCellId]) {
        let mergedCellsId = this.mergedCellIds[this.activeCellId].replace("_merged", "");
        let [columnIndex, rowIndex] = this.getCellIndexById(mergedCellsId);
        columnLeft =
          this.getPxFromColumnIndex(columnIndex) + RowNumberWidth + containerBound.left - container.scrollLeft;
        rowTop = this.getPxFromRowIndex(rowIndex) + ColumnLetterHeight + containerBound.top - container.scrollTop;
      } else {
        columnLeft =
          this.getPxFromColumnIndex(this.columnIndex) + RowNumberWidth + containerBound.left - container.scrollLeft;
        rowTop = this.getPxFromRowIndex(this.rowIndex) + ColumnLetterHeight + containerBound.top - container.scrollTop;
      }
      return {
        top: `${rowTop}px`,
        left: `${columnLeft}px`,
      };
    },
    cellHeight() {
      return this.getRawHeight(this.rowId);
    },
    cellWidth() {
      return this.getRawWidth(this.columnId);
    },
    row() {
      return this.rows[this.rowIndex];
    },
    column() {
      return this.columns[this.columnIndex];
    },
    cell() {
      return this.cellsMetaData[this.activeCellId] || {};
    },
    cellType() {
      return this.cell?.type || getDefaultCellType(this.row.type, this.column.type);
    },
    mergeId() {
      return this.mergedCellIds[this.activeCellId];
    },
    activeCellIndexes() {
      return this.getRangeByCellId(this.activeCellId);
    },
    hScroll() {
      return this.$store.state.cells.hScroll;
    },
    vScroll() {
      return this.$store.state.cells.vScroll;
    },
    isActiveCellLabelVisible() {
      return this.$store.state.cells.isActiveCellLabelVisible;
    },
    refinedActiveCellIndexes() {
      const maxHScroll = this.hScroll.end - this.hScroll.index;
      const maxVScroll = this.vScroll.end - this.vScroll.index;

      return [
        Math.max(0, Math.min(maxHScroll, this.activeCellIndexes[0] - this.hScroll.index)),
        Math.max(0, Math.min(maxVScroll, this.activeCellIndexes[1] - this.vScroll.index)),
        Math.min(maxHScroll, Math.max(0, this.activeCellIndexes[2] - this.hScroll.index)),
        Math.min(maxVScroll, Math.max(0, this.activeCellIndexes[3] - this.vScroll.index)),
      ];
    },
    tableNameRangesMap() {
      return this.$store.state.table.tableNameRangesMap;
    },
    ranges() {
      return this.$store.getters["cells/ranges"]();
    },
    customStyles() {
      const style = this.$store.getters["rangeStyle/properties"](this.columnIndex, this.rowIndex);
      const { bgColor, color, fontSize, fontFamily, fontWeight, verticalAlign } = style;
      return {
        "background-color": bgColor,
        color,
        "font-size": `${fontSize}px`,
        "font-family": fontFamily,
        "font-weight": fontWeight,
        "align-content": verticalAlign,
      };
    },
    textAlign() {
      const style = this.cellsMetaData[this.activeCellId]?.style;
      if (!style) return !isNaN(parseFloat(this.text)) ? "right" : "";

      const { textAlign } = JSON.parse(style);
      return textAlign ?? (!isNaN(parseFloat(this.text)) ? "right" : "");
    },
    themeClass() {
      const theme = defaultThemes[this.cellType];
      return `kk-designer__cell-theme-${theme}`;
    },
    zIndex() {
      // when in edit mode the arrow should go behind otherwise arrow should be visible arrow z-index is 15
      return this.editMode !== EditModes.Disable ? 16 : 14;
    },
    expandibleElementStyle() {
      return { width: `${this.dynamicWidth}px`, height: `${this.dynamicHeight}px` };
    },
    textareaStyle() {
      return {
        resize: "none",
        overflow: "hidden",
        whiteSpace: "nowrap",
      };
    },
  },
  watch: {
    activeCellId(newValue, oldValue) {
      this.reset();
      this.initializeData(newValue);
      this.$store.dispatch("cells/setActiveCellLabel", false);
    },
    editCell(newValue, oldValue) {
      this.text = newValue.text;
      this.setTextAreaRows();
      this.$nextTick(() => {
        const textarea = this.$refs.textarea;
        textarea?.setSelectionRange(newValue.cursorPosition, newValue.cursorPosition);
        const textWidth = textarea.scrollWidth;
        const visibleWidth = textarea.clientWidth;
        if (newValue.formulaBox && textWidth > visibleWidth) {
          textarea.scrollLeft = textarea.scrollWidth; // Scroll the input field to the cursor position
        }
      });
    },
    rangeDragging(newValue, oldValue) {
      if (newValue.id) {
        this.$refs.textarea?.focus();
        this.$nextTick(() => {
          this.$refs.textarea?.setSelectionRange(this.editCell?.text.length, this.editCell?.text.length);
        });
      }
    },
    // Object is only used for calling watch in case of view changes
    editModeObject(newValue, oldValue) {
      this.setTextAreaRows();
    },
    editMode(newValue, oldValue) {
      this.setTextAreaRows();
    },
  },
  created() {
    this.setDefaultValue();
  },
  mounted() {
    if (!this.editCell.focus) {
      this.$refs.textarea?.focus();
    }
    this.initializeData(this.editCell.id);
    if (this.editMode === EditModes.InnerEdit) {
      let cellId = this.editCell.id;
      if (this.mergedCellIds[cellId]) cellId = this.mergedCellIds[cellId].replace("_merged", "");
      const [columnIndex, rowIndex] = this.getCellIndexById(`${cellId}`);
      const currentDataType = this.$store.getters["rangeStyle/dataType"](columnIndex, rowIndex);
      this.text = getEditModeValue(currentDataType, this.text);
      this.$refs.textarea.focus();
      this.$nextTick(() => {
        this.$refs.textarea?.setSelectionRange(this.text?.length, this.text?.length);
      });
    }
    this.$store.dispatch("cells/setFormulaRange", { title: this.editCell.text, isMainRange: false });
  },
  methods: {
    onShowComment() {
      this.onHover(this.cell, this.id);
    },
    getCellValueById(cellId) {
      const [columnIndex, rowIndex] = this.getCellIndexById(cellId);
      return hfInstance.getCellValue({
        sheet: this.currentSheet.sheetId,
        row: rowIndex,
        col: columnIndex,
      });
    },
    async handleKeyDown(event) {
      if (hotkeys.getScope() === HotkeyScopes.QuickAccessToolbar) return;

      // Use shared hotkey handling
      if (this.handleHotkeys(event)) return;
      if (keycodeToDirection[event.key]) {
        this.handleDirectionKeys(event);
      } else if (event.key === "Enter") {
        await this.handleEnterKey(event);
      } else if (event.key === "Tab") {
        this.handleTabKey(event);
      } else if (event.key === "Escape" && this.editMode !== EditModes.Disable) {
        this.handleEscapeKey();
      } else if (event.ctrlKey && event.key === ".") hotkeys.trigger("ctrl+.", HotkeyScopes.ProcessDesigner);
      else if (event.key === "F4") {
        this.handleF4Key(event);
      } else if (event.key === "F2") {
        this.handleF2Key(event);
      }

      this.$nextTick(() => {
        if (this.$refs.textarea) {
          this.setTextAreaRows();
        }
      });
    },
    async handleEnterKey(event) {
      event.preventDefault();

      if (event.ctrlKey) {
        await this.processCtrlEnter();
        this.reset();
      } else if (event.altKey) {
        this.insertNewLine(event);
      } else if (event.shiftKey) {
        hotkeys.trigger("shift+enter", HotkeyScopes.ProcessDesigner);
      }
      // else if (this.showSearchBox && this.dropDownFilteredList.length > 0) {
      //   hotkeys.trigger("tab", HotkeyScopes.FormulaSearch);
      // }
      else {
        if (!this.isFormulaMode) {
          await this.saveCellContent(this.activeCellId, this.text);
          this.isSaved = true;
        }
        this.reset();
        hotkeys.trigger("enter", HotkeyScopes.ProcessDesigner);
      }

      this.formulaText = "";
      this.setTextAreaRows();
    },
    handleF2Key(event) {
      let cursorPosition = event.target.selectionStart;

      if (this.editMode === EditModes.InnerEdit) {
        this.$store.dispatch("pdesign/setEditMode", EditModes.OuterEdit);
        this.$store.dispatch("cells/setEditCell", { cursorPosition });
      } else {
        this.$store.dispatch("pdesign/setEditMode", EditModes.InnerEdit);
        this.$store.dispatch("cells/setEditCell", { range: "" });
      }
    },
    handleDirectionKeys(event) {
      // If basic direction key handling is done, return
      if (this.handleBasicDirectionKeys(event)) return;

      if (this.editMode === EditModes.InnerEdit) {
        if (hotkeys.getScope() === HotkeyScopes.FormulaSearch) {
          event.preventDefault();
          hotkeys.trigger(keycodeToDirection[event.key], HotkeyScopes.FormulaSearch);
        }
        // Text area defaults actions is working fine
      } else {
        this.$store.dispatch("cells/setEditCell", {
          cursorPosition: event.target.selectionStart,
          formulaRangeSelectionAllowed: isRangeSelectionAllowed(
            event.target.value.substring(0, event.target.selectionStart)
          ),
          range: "",
        });
      }
    },
    handleEscapeKey() {
      this.showSearchBox = false;
      this.text = this.originalText;

      this.$store.dispatch("cells/setEditCell", { text: this.text });
      this.$store.dispatch("cells/setFormulaRange", {
        title: `${cellIndexToLabel(this.columnIndex, this.rowIndex)}`,
      });
      this.$store.dispatch("cells/setActiveRangeMainCellCoordinate", this.getCellCoordinate(this.editCell.id));

      this.$nextTick(() => {
        this.reset();
      });
    },
    initializeData(cellId) {
      if (this.mergedCellIds[cellId]) cellId = this.mergedCellIds[cellId].replace("_merged", "");
      const [columnIndex, rowIndex] = this.getCellIndexById(`${cellId}`);
      const text = this.getCellText(rowIndex, columnIndex);
      this.originalText = text;
      this.text = this.editCell.text;
      this.id = cellId;
      this.setTextAreaRows();
    },
    setTextAreaRows() {
      this.textAreaRows = (String(this.text).match(/\n/g)?.length || 0) + 1;
      this.setAreaDimentions();
    },
    reset() {
      this.$store.dispatch("pdesign/setEditMode", EditModes.Disable);
      this.$refs.textarea?.blur();
      this.argNumber = 0;
      this.collection = [...this.allFormulaNames];
      this.showSearchBox = false;
    },
    setDefaultValue() {
      this.dynamicHeight =
        (this.mergeId ? this.getMergedCellHeight(this.mergeId) : this.cellHeight) -
        ((this.options.margin ?? 0) * 2).toFixed(1);
      this.dynamicWidth =
        (this.mergeId ? this.getMergedCellWidth(this.mergeId) : this.cellWidth) -
        ((this.options.margin ?? 0) * 2).toFixed(1);
    },
    setAreaDimentions() {
      const textarea = this.$refs.textarea;
      if (!textarea) return;
      const fontSize = this.getComputedStyleProperty(textarea, "font-size");
      const fontFamily = this.getComputedStyleProperty(textarea, "font-family");
      const lineHeight = this.getComputedStyleProperty(textarea, "line-height");
      const padding = this.getComputedStyleProperty(textarea, "padding");
      var cellHeight =
        (this.mergeId ? this.getMergedCellHeight(this.mergeId) : this.cellHeight) -
        ((this.options.margin ?? 0) * 2).toFixed(1);
      var cellWidth =
        (this.mergeId ? this.getMergedCellWidth(this.mergeId) : this.cellWidth) -
        ((this.options.margin ?? 0) * 2).toFixed(1);

      const { width, height } = this.getElementDimensions(this.text ?? "", fontSize, fontFamily, lineHeight, padding);
      if (this.editMode !== EditModes.Disable) {
        this.dynamicWidth = width > cellWidth - 10 ? width + 10 : cellWidth;
        this.dynamicHeight = height > cellHeight ? height : cellHeight;
      } else {
        this.dynamicWidth = cellWidth;
        this.dynamicHeight = cellHeight;
      }
    },
    getComputedStyleProperty(element, property) {
      return window.getComputedStyle(element, null).getPropertyValue(property);
    },
    getElementDimensions(content, fontSize, fontFamily, lineHeight, padding) {
      const measuringElement = document.createElement("pre");
      measuringElement.style.position = "absolute";
      measuringElement.style.visibility = "hidden";
      measuringElement.style.whiteSpace = "pre";
      measuringElement.style.fontSize = fontSize;
      measuringElement.style.fontFamily = fontFamily;
      measuringElement.style.lineHeight = lineHeight;
      measuringElement.style.padding = padding;

      measuringElement.innerText = content + "a"; // Adding this character will solve the problem with white spaces in new line for height calculations

      document.body.appendChild(measuringElement);

      const width = measuringElement.offsetWidth;
      const height = measuringElement.offsetHeight;
      document.body.removeChild(measuringElement);

      return { width, height };
    },
    getMaxRowCharacterCount() {
      const value = this.text ?? "";
      const rows = value.split("\n");
      let maxRowCharacterCount = 0;

      rows.forEach((row) => {
        if (row.length > maxRowCharacterCount) {
          maxRowCharacterCount = row.length;
        }
      });

      return maxRowCharacterCount;
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../assets/styles/variables";

.kk-editcell {
  // margin: 0.2rem;
  border-radius: 0.25rem;
  z-index: 14;
  position: fixed;
  border-style: solid;
  border-width: 2px;
  border-color: transparent;
  display: grid;
  grid-template-columns: 1fr;
  box-shadow: rgba(0, 0, 0, 0.4) 1px 2px 5px;
  border-color: #5292f7;
  & textarea,
  & a {
    overflow: hidden;
    padding: 0 4px;
    // line-height: 1.2;
  }
  & a {
    width: fit-content;
  }
  & textarea {
    width: 100%;
    resize: none;
    color: inherit;
    &:focus {
      outline: none;
    }
  }
  &-pointer-disable {
    cursor: default;
  }
  &-inward-cursor {
    caret-color: red;
  }
  &-comment {
    position: absolute;
    top: 0;
    right: 0;
    border-left: 9px solid transparent;
    border-bottom: 9px solid red;
    transform: rotate(-90deg);
    border-bottom-right-radius: @kk-border-radius;
  }
  &-icons {
    position: absolute;
    top: 50%;
    right: 2px;
    transform: translateY(-50%);
    width: fit-content;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.2rem;
  }

  &__activeCellLabel {
    position: absolute;
    top: -23px;
    left: -2px;
    padding: 0 6px;
    background: @kk-btn-info-color;
    color: @kk-color-white;
  }
}
</style>
